import { Pencil, Trash2 } from "lucide-react";
import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const normalizeCategoryLabel = (category) => {
  if (!category) return "Uncategorized";
  return category.charAt(0).toUpperCase() + category.slice(1);
};

const SurveyQuestionList = ({
  questions = [],
  categories = [],
  disabled = false,
  onEdit,
  onDelete,
}) => {
  const { t } = useTran();
  const categorySet = new Set([
    ...(categories || []),
    ...questions.map((question) => question.category || "uncategorized"),
  ]);
  const orderedCategories = Array.from(categorySet);

  if (!questions.length) {
    return (
      <div className="rounded-radius border border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground shadow-sm">
        {t('No questions yet. Use "Add Question" to create your first one.')}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {orderedCategories.map((category) => {
        const catKey = category || "uncategorized";
        const catQuestions = questions
          .map((question, index) => ({ ...question, index }))
          .filter(
            (question) => (question.category || "uncategorized") === catKey,
          );
        const displayLabel = normalizeCategoryLabel(catKey);
        return (
          <div
            key={catKey}
            className="space-y-2 rounded-radius border border-border bg-muted/40 p-4 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {displayLabel}
              </span>
              <span className="rounded-full bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground shadow-sm">
                {catQuestions.length}{" "}
                {catQuestions.length === 1 ? t("question") : t("questions")}
              </span>
            </div>
            {catQuestions.length ? (
              <ul className="space-y-2">
                {catQuestions.map((question) => (
                  <li
                    key={question.index}
                    className="rounded-radius border border-border bg-card p-2 text-xs text-foreground shadow-sm"
                  >
                    <p className="line-clamp-2 font-semibold">
                      {question.questionText || t("Untitled question")}
                    </p>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold capitalize text-primary">
                          {question.questionType?.replace("_", " ") || "text"}
                        </span>
                        {(question.originType === "standard" ||
                          question.questionBankId) && (
                          <Badge variant="secondary" className="text-[11px]">
                            {t("Standard")}
                          </Badge>
                        )}
                      </div>
                      {!disabled ? (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            onClick={() => onEdit?.(question.index)}
                            size="sm"
                            variant="outline"
                          >
                            <Pencil className="h-3 w-3" />
                            {t("Edit")}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => onDelete?.(question.index)}
                            size="sm"
                            variant="destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                            {t("Delete")}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                {t("No questions in this category yet.")}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SurveyQuestionList;
